/**
 * ============================================================
 * KEJJA OS — src/pages/routines/RoutinesLayout.tsx
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { Baby, Repeat, CheckCircle2, List } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface RoutinesContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;
}

export function useRoutinesContext(): RoutinesContext {
  return useOutletContext<RoutinesContext>();
}

const TABS = [
  { to: '/routines',        label: 'Today',    icon: CheckCircle2, end: true },
  { to: '/routines/kids', label: 'Kids', icon: Baby },
  { to: '/routines/manage', label: 'Manage',   icon: List },
];

export default function RoutinesLayout() {
  const { data, isLoading } = useMyHomes();
  const first = data?.homes?.[0];
  const homeId = first?.home.id;
  const currentUserId = undefined;  // TODO: wire from /me if a page needs it
  const homeName = first?.home.name ?? '';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!homeId) {
    return <div className="text-sm text-muted-foreground">No home found. Create a home first.</div>;
  }

  return (
    <div className="space-y-6">
      {/* ============ HEADER ============ */}
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-teal-100 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 flex items-center justify-center flex-shrink-0">
            <Repeat className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
              Routines
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="italic">Rhythm beats willpower.</span>
              {homeName && <span className="ml-2">· {homeName}</span>}
            </p>
          </div>
        </div>
      </header>

      {/* ============ SUB-NAV ============ */}
      <nav className="flex items-center gap-1">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-teal-100 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ homeId, currentUserId, homeName } satisfies RoutinesContext} />
    </div>
  );
}
